/**
 * <FOGuestWorkspaceContainer>
 * -------------------------------------------------------------
 * Owns the loading + tab state around the ODS <GuestWorkspace> primitive.
 * Every FO action drawer (CheckIn / CheckOut / RoomChange / QuickCharge /
 * QuickPayment / ExtendStay / CommLog) is launched from here — never
 * duplicated elsewhere (ADR-FO-1 AR-7 · §0A).
 */
import { useCallback, useEffect, useState } from "react";
import { useCheckinGate } from "@/hooks/useCheckinGate";
import { toast } from "sonner";
import {
  GuestWorkspace, StatusChip,
} from "@/components/operational";
import { foApi, money } from "./foApi";
import { requestRegCard } from "@/components/documents/RegCardOptions";
import {
  LogIn, LogOut, ArrowRightLeft, Receipt, CreditCard, StickyNote,
  CalendarClock, Printer, BedDouble, RefreshCw,
} from "lucide-react";

import CheckInDrawer from "./drawers/CheckInDrawer";
import { CheckoutFlow } from "@/components/checkout/CheckoutFlow";
import RoomChangeModal from "@/components/pms/RoomChangeModal";
import QuickChargeDrawer from "./drawers/QuickChargeDrawer";
import QuickPaymentDrawer from "./drawers/QuickPaymentDrawer";
import ExtendStayDrawer from "./drawers/ExtendStayDrawer";
import CommunicationLogDrawer from "./drawers/CommunicationLogDrawer";

import {
  OverviewTab, ReservationTab, StayTab, RoomTab, FolioTab,
  PaymentsTab, TimelineTab, DocumentsTab, NotesTab,
} from "./tabs/index.jsx";

export default function FOGuestWorkspaceContainer({
  guestId, open, onOpenChange,
  initialTab = "overview",
  initialAction = null, // "check-in" | "check-out" | "room-change" | "quick-charge" | "quick-payment" | "extend" | "note"
}) {
  const [workspace, setWorkspace] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [action, setAction] = useState(initialAction);
  const gate = useCheckinGate();

  const load = useCallback(async () => {
    if (!guestId) return;
    setLoading(true);
    setError(null);
    try {
      const [w, t] = await Promise.all([
        foApi.workspace(guestId),
        foApi.timeline(guestId, { limit: 200 }),
      ]);
      setWorkspace(w);
      setTimeline(t.events || []);
    } catch (e) {
      setError({ title: "Failed to load guest", body: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [guestId]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { setTab(initialTab); setAction(initialAction); }, [initialTab, initialAction, guestId]);

  const g = workspace?.guest;
  const stay = workspace?.active_stay;
  const folio = workspace?.active_folio;
  const room = workspace?.active_room;

  const headerStatus = stay
    ? <StatusChip status={stay.status === "checked_in" ? "active" : (stay.status === "reserved" ? "pending" : "ready")}
        label={stay.status} size="sm" testid="workspace-status"/>
    : null;

  const checkinGated = gate.isBlocked(stay?.expected_arrival);
  const secondaryActions = [
    stay?.status === "reserved" && {
      label: "Check-In", sublabel: checkinGated ? gate.reason(stay.expected_arrival) : undefined, icon: LogIn, tone: "primary",
      onClick: () => setAction("check-in"), testid: "gw-action-checkin", disabled: checkinGated,
    },
    stay?.status === "checked_in" && {
      label: "Check-Out", icon: LogOut, tone: "primary",
      onClick: () => setAction("check-out"), testid: "gw-action-checkout",
    },
    stay?.status === "checked_in" && {
      label: "Room Change", icon: ArrowRightLeft, tone: "secondary",
      onClick: () => setAction("room-change"), testid: "gw-action-roomchange",
    },
    (stay?.status === "reserved" || stay?.status === "checked_in") && {
      label: "Extend Stay", icon: CalendarClock, tone: "secondary",
      onClick: () => setAction("extend"), testid: "gw-action-extend",
    },
    (folio) && {
      label: "Quick Charge", icon: Receipt, tone: "secondary",
      onClick: () => setAction("quick-charge"), testid: "gw-action-charge",
    },
    (folio) && {
      label: "Take Payment", icon: CreditCard, tone: "success",
      onClick: () => setAction("quick-payment"), testid: "gw-action-payment",
    },
    {
      label: "Add Note", icon: StickyNote, tone: "secondary",
      onClick: () => setAction("note"), testid: "gw-action-note",
    },
    stay?.id && {
      label: "Print Reg Card", icon: Printer, tone: "secondary",
      onClick: () => requestRegCard({ url: foApi.registrationCardUrl(stay.id), filename: `RegistrationCard-${stay.code || stay.id}.pdf` }),
      testid: "gw-action-print-regcard",
    },
  ].filter(Boolean);

  const primaryAction = {
    label: "Refresh", icon: RefreshCw, tone: "secondary", onClick: load,
    testid: "gw-refresh",
  };

  const closeDrawer = () => setAction(null);
  const onSuccess = async () => {
    setAction(null);
    await load();
  };

  return (
    <>
      <GuestWorkspace
        open={open}
        onOpenChange={onOpenChange}
        guest={g ? {
          id: g.id,
          name: g.display_name || `${g.first_name || ""} ${g.last_name || ""}`.trim(),
          vip: g.is_vip_effective,
          primary_phone: g.primary_phone || g.phone,
          primary_email: g.primary_email || g.email,
        } : null}
        headerStatus={headerStatus}
        activeTab={tab}
        onTabChange={setTab}
        loading={loading}
        error={error}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
        testid="fo-guest-workspace"
      >
        {(activeTab) => {
          const props = { workspace, timeline, guestId, money, onOpenAction: setAction, reload: load };
          switch (activeTab) {
            case "overview":    return <OverviewTab {...props}/>;
            case "reservation": return <ReservationTab {...props}/>;
            case "stay":        return <StayTab {...props}/>;
            case "room":        return <RoomTab {...props}/>;
            case "folio":       return <FolioTab {...props}/>;
            case "payments":    return <PaymentsTab {...props}/>;
            case "timeline":    return <TimelineTab {...props}/>;
            case "documents":   return <DocumentsTab {...props}/>;
            case "notes":       return <NotesTab {...props}/>;
            default:            return null;
          }
        }}
      </GuestWorkspace>

      {/* Action drawers — all launched from workspace (AR-7) */}
      {stay && (
        <>
          <CheckInDrawer open={action === "check-in" && stay.status !== "checked_in" && !checkinGated} onOpenChange={closeDrawer} stay={stay}
                          onSuccess={() => { toast.success("Guest checked in"); onSuccess(); }}/>
          {action === "check-out" && stay.status === "checked_in" && (
            <CheckoutFlow stayId={stay.id} hotelId={stay.hotel_id} open onCancel={closeDrawer}
                          onSuccess={() => { toast.success("Guest checked out"); closeDrawer(); onSuccess(); }}/>
          )}
          <RoomChangeModal open={action === "room-change"} onOpenChange={closeDrawer}
                           stayId={stay.id} hotelId={stay.hotel_id} currentRoomId={stay.current_room_id}
                           currentRoomNumber={stay.room_number || stay.current_room_number} stayCode={stay.code}
                           onSuccess={() => { toast.success("Room changed"); onSuccess(); }}/>
          <ExtendStayDrawer open={action === "extend"} onOpenChange={closeDrawer} stay={stay}
                              onSuccess={() => { toast.success("Stay extended"); onSuccess(); }}/>
        </>
      )}
      {folio && (
        <>
          <QuickChargeDrawer open={action === "quick-charge"} onOpenChange={closeDrawer} folio={folio} stay={stay}
                               onSuccess={() => { toast.success("Charge posted"); onSuccess(); }}/>
          <QuickPaymentDrawer open={action === "quick-payment"} onOpenChange={closeDrawer} folio={folio} stay={stay}
                                onSuccess={() => { toast.success("Payment recorded"); onSuccess(); }}/>
        </>
      )}
      <CommunicationLogDrawer
        open={action === "note"} onOpenChange={closeDrawer}
        guest={g} stay={stay} room={room}
        onSuccess={() => { toast.success("Note saved"); onSuccess(); }}/>
    </>
  );
}
